'use client';
import { EventLocationDropdown } from '@v2/containers/EventsCarouselByLocation/EventLocationDropdown';
import { AWSLocationResponse, TeamProps } from '@v2/types';
import { useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { EventResultsUiConfig } from '../constants/eventPageUiTypes';
import {
  HomeAwayEventsTab,
  type HomeAwayEventsTypes,
} from '../components/HomeAwayEventsTab';
import { CONCERTS_ID } from '../util';
import { DateRangePicker } from './DateRangePicker';
import { OponnetsDropdownWrapper } from './OponnetsDropdownWrapper';

interface Props {
  onLocationChange: (location: AWSLocationResponse) => void;
  onDateChange: (startDate?: string, endDate?: string) => void;
  onHomeAwayChange?: (tab: HomeAwayEventsTypes) => void;
  onOpponentChange: (team?: TeamProps) => void;
  isTeamPage?: boolean;
  teamSlug?: string;
  leagueSlug?: string;
  eventResultsConfig?: EventResultsUiConfig;
}

const DEFAULT_LOCATION = {
  Text: 'Miami, FL, United States',
  PlaceId:
    'AQABAFMAP3RXXM3qdNIK2xpZw8lM2jWXLNO8qoVFkTkpp8Ik95drqyiD7WOwd1d77WbjIjCRTT_-Ghnk9t7DWJ6KrHlJlbEBCm93u4DEZjD8CanLe1GFKLOtyYWePb-zqqoKAb3WnC8XhfDJxZfaKy8rSi2Q5HPZnw',
  Categories: ['MunicipalityType'],
};

export const EventsFilters = ({
  onLocationChange,
  onDateChange,
  onHomeAwayChange,
  onOpponentChange,
  isTeamPage = false,
  teamSlug,
  leagueSlug,
  eventResultsConfig,
}: Props) => {
  const [selectedLocation, setSelectedLocation] =
    useState<AWSLocationResponse>();
  const searchParams = useSearchParams();

  const parentId = searchParams?.get('parentId');
  const opponentIdParam = searchParams?.get('opponentId');

  const isConcerts = CONCERTS_ID.includes(Number(parentId));
  const hideLocationFilter = eventResultsConfig?.hideLocationFilter ?? false;
  const compactMobileFilters =
    eventResultsConfig?.compactMobileFilters ?? false;

  const [selectedDateFilter, setSelectedDateFilter] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [selectedOpponentFilter, setSelectedOpponentFilter] = useState<any>();

  const handleChangeLocation = useCallback((location: AWSLocationResponse) => {
    onLocationChange(location);
    setSelectedLocation(location);
  }, []);

  const onDateFilterChange = useCallback((dateFilter: DateRange) => {
    setSelectedDateFilter(dateFilter);

    const formatDate = (date?: Date) => {
      if (!date) return undefined;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    onDateChange(formatDate(dateFilter.from), formatDate(dateFilter.to));
  }, []);

  const handleOpponentChange = useCallback((team) => {
    onOpponentChange(team);
    setSelectedOpponentFilter(team);
  }, []);

  const isResetButtonVisible =
    !!selectedDateFilter.from ||
    !!selectedDateFilter.to ||
    (!hideLocationFilter && !!selectedLocation) ||
    !!selectedOpponentFilter;

  return (
    <div
      className={`no-scrollbar flex h-auto ${
        compactMobileFilters
          ? 'flex-col gap-3 overflow-visible md:mb-6 md:flex-row md:items-start md:justify-between'
          : 'flex-row justify-between overflow-scroll md:mb-6 md:overflow-hidden'
      }`}
    >
      <div
        className={`flex ${compactMobileFilters ? 'flex-wrap gap-3' : 'flex-row gap-x-3'}`}
      >
        {!hideLocationFilter && (
          <EventLocationDropdown
            selectedLocation={selectedLocation}
            variant="list"
            onLocationChange={handleChangeLocation}
          />
        )}
        <DateRangePicker
          selectedDateFilter={selectedDateFilter}
          onDateChange={onDateFilterChange}
          defaultLabel={eventResultsConfig?.dateFilterDefaultLabel}
          compactMobile={compactMobileFilters}
        />
        {!isConcerts && isTeamPage && !opponentIdParam && (
          <OponnetsDropdownWrapper
            leagueSlug={leagueSlug}
            teamSlug={teamSlug}
            selectedValue={selectedOpponentFilter}
            onOpponentChange={handleOpponentChange}
            defaultLabel={eventResultsConfig?.opponentFilterDefaultLabel}
            compactMobile={compactMobileFilters}
          />
        )}
        {isResetButtonVisible && (
          <button
            onClick={() => {
              setSelectedLocation(undefined);
              onLocationChange(DEFAULT_LOCATION);
              onDateChange(undefined, undefined);
              setSelectedDateFilter({
                from: undefined,
                to: undefined,
              });
              if (selectedOpponentFilter) {
                setSelectedOpponentFilter(undefined);
                onOpponentChange(undefined);
              }
            }}
            className={`flex h-9 shrink-0 items-center rounded-lg text-gray-800 ${
              compactMobileFilters
                ? 'px-3 text-sm md:h-12 md:px-4 md:text-base'
                : 'px-4 md:h-12'
            }`}
          >
            Reset filters
          </button>
        )}
      </div>
      {!isConcerts && isTeamPage && onHomeAwayChange && (
        <HomeAwayEventsTab onTabChange={onHomeAwayChange} />
      )}
    </div>
  );
};
